import React, { useEffect, useRef, useState } from 'react';
import { SimulationEngine } from '../../engine/SimulationEngine';
import SimulationView3D from './SimulationView3D';

export default function SimulationView({ mapData, config, setMetricsHistory }) {
  const tradCanvasRef = useRef(null);
  const aiCanvasRef = useRef(null);
  const engineRef = useRef({ trad: null, ai: null });
  const orderTicksRef = useRef({});
  const [isPlaying, setIsPlaying] = useState(false);
  const [viewMode, setViewMode] = useState('2d'); // '2d' or '3d'
  const reqRef = useRef(null);
  const [metrics, setMetrics] = useState({ trad: null, ai: null });
  const [bgImage, setBgImage] = useState(null);

  useEffect(() => {
    if (mapData.bgImageData) {
      const img = new Image();
      img.onload = () => setBgImage(img);
      img.src = mapData.bgImageData;
    }
  }, [mapData.bgImageData]);

  useEffect(() => {
    if (mapData.nodes.length > 0) {
      engineRef.current.trad = new SimulationEngine(mapData, config, 'traditional');
      engineRef.current.ai = new SimulationEngine(mapData, config, 'ai');
    }
  }, [mapData, config]);

  const formatTime = (ticks) => {
    if (!ticks) return "00:00:00";
    const totalSeconds = Math.floor(ticks / 60);
    const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
  };

  const formatTicksToMins = (ticks) => {
    if (!ticks) return "0 mins";
    const mins = (ticks / 3600).toFixed(1);
    return `${mins} mins`;
  };

  const drawEngineState = (canvas, engine) => {
    if (!canvas || !engine) return;
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // 1. Draw Background Image
    if (bgImage) {
      // Fill white background first so opacity fades to white
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      const s = Math.min(canvas.width / bgImage.width, canvas.height / bgImage.height);
      const ox = (canvas.width - bgImage.width * s) / 2;
      const oy = (canvas.height - bgImage.height * s) / 2;
      
      const opacity = mapData.bgSettings?.opacity ?? 1;
      const grayscale = mapData.bgSettings?.grayscale ?? 0;
      const rotation = mapData.bgSettings?.rotation ?? 0;
      
      ctx.save();
      ctx.globalAlpha = opacity;
      if (grayscale > 0) {
        ctx.filter = `grayscale(${grayscale}%)`;
      }
      
      if (rotation !== 0) {
        ctx.translate(canvas.width / 2, canvas.height / 2);
        ctx.rotate(rotation * Math.PI / 180);
        ctx.translate(-canvas.width / 2, -canvas.height / 2);
      }
      
      ctx.drawImage(bgImage, ox, oy, bgImage.width * s, bgImage.height * s);
      ctx.restore();
    } else {
      ctx.fillStyle = '#f9fafb';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    }

    // 2. Draw Areas (Storage, Pickup, Dropoff)
    mapData.areas.forEach(area => {
      let fillColor = 'rgba(139, 92, 246, 0.25)'; // Storage (Purple)
      let strokeColor = 'rgba(139, 92, 246, 0.5)';
      if (area.type === 'pickup') { 
        fillColor = 'rgba(16, 185, 129, 0.25)'; 
        strokeColor = 'rgba(16, 185, 129, 0.5)';
      }
      if (area.type === 'dropoff') { 
        fillColor = 'rgba(239, 68, 68, 0.25)'; 
        strokeColor = 'rgba(239, 68, 68, 0.5)';
      }
      
      ctx.fillStyle = fillColor;
      ctx.fillRect(area.x, area.y, area.w, area.h);
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = 1;
      ctx.strokeRect(area.x, area.y, area.w, area.h);
    });

    // 3. Draw Corridors
    (mapData.corridors || []).forEach(cor => {
      ctx.fillStyle = 'rgba(251,191,36,0.2)';
      ctx.fillRect(cor.x, cor.y, cor.w, cor.h);
      ctx.strokeStyle = 'rgba(245,158,11,0.5)';
      ctx.lineWidth = 2;
      ctx.strokeRect(cor.x, cor.y, cor.w, cor.h);
    });

    // 4. Draw Edges
    // 4. Draw Auto-Generated Edges (Lane centerlines)
    ctx.strokeStyle = 'rgba(100, 100, 100, 0.4)';
    ctx.lineWidth = 1;
    ctx.setLineDash([5, 5]);
    (mapData.edges || []).forEach(edge => {
      const from = mapData.nodes.find(n => n.id === edge.from);
      const to = mapData.nodes.find(n => n.id === edge.to);
      if (from && to) {
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.stroke();
        
        // Draw direction arrow if one-way
        if (edge.dir === 'uni') {
           const midX = (from.x + to.x) / 2;
           const midY = (from.y + to.y) / 2;
           const angle = Math.atan2(to.y - from.y, to.x - from.x);
           ctx.save();
           ctx.translate(midX, midY);
           ctx.rotate(angle);
           ctx.beginPath();
           ctx.moveTo(0, 0);
           ctx.lineTo(-4, -3);
           ctx.lineTo(-4, 3);
           ctx.closePath();
           ctx.fillStyle = 'rgba(100, 100, 100, 0.6)';
           ctx.fill();
           ctx.restore();
        }
      }
    });
    ctx.setLineDash([]);

    // Draw Intersection Nodes
    (mapData.nodes || []).filter(n => n.type === 'intersection').forEach(n => {
      ctx.beginPath();
      ctx.arc(n.x, n.y, 4, 0, 2 * Math.PI);
      ctx.fillStyle = '#f59e0b';
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 1.5;
      ctx.stroke();
    });

    // 5. Draw Occupied Slots (Pallets)
    const state = engine.getState();
    (state.virtualSlots || []).forEach(slot => {
      if (state.occupiedNodes && state.occupiedNodes.has(slot.id)) {
         // Amber pallet
         ctx.fillStyle = '#f59e0b';
         ctx.fillRect(slot.x - 8, slot.y - 8, 16, 16);
      }
    });

    // Draw orders waiting at pickup (Green Pallets)
    if (state.orders && state.virtualSlots) {
       state.orders.forEach(order => {
          const isPickedUp = state.agvs.some(agv => agv.payload && agv.payload.id === order.id && agv.state === 'moving_to_storage');
          if (!isPickedUp) {
             const slot = state.virtualSlots.find(s => s.id === order.pickupNodeId);
             if (slot) {
                // Green pallet
                ctx.fillStyle = '#10b981';
                ctx.fillRect(slot.x - 8, slot.y - 8, 16, 16);
             }
          }
       });
    }

    // 6. Draw AGVs
    state.agvs.forEach(agv => {
      // Base AGV (idle=gray, moving=blue)
      const agvColor = agv.state === 'idle' ? '#6b7280' : (config.agvColor || '#3b82f6');
      
      ctx.fillStyle = agvColor;
      ctx.fillRect(agv.x - 10, agv.y - 10, 20, 20);
      
      // Payload if carrying something (Amber pallet on top of AGV)
      if (agv.payload) {
         ctx.fillStyle = '#f59e0b';
         ctx.fillRect(agv.x - 6, agv.y - 6, 12, 12);
      }

      // Label
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 10px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText(agv.id.replace('agv_', ''), agv.x, agv.y + 3);
    });
  };

  const tick = () => {
    if (engineRef.current.trad && engineRef.current.ai) {
      const simSpeed = parseInt(config.simSpeed) || 1;
      
      for (let s = 0; s < simSpeed; s++) {
        // Evaluate order generation probabilities centrally so both engines get the exact same order events
        const validFlows = (mapData.flows || []).filter(f => 
          (f.sourceAreaId || (f.sourceAreaIds && f.sourceAreaIds.length > 0)) && 
          (f.targetAreaId || (f.targetAreaIds && f.targetAreaIds.length > 0))
        );
        
        if (validFlows.length > 0) {
          for (const flow of validFlows) {
            const flowConfig = (config.flowSettings || {})[flow.id] || { orderRate: config.orderRate || 10 };
            const genType = flowConfig.orderGenerationType || 'random';
            const ratePerHour = flowConfig.orderRate;
            
            if (genType === 'random') {
              const probabilityPerTick = (ratePerHour / 3600) / 60;
              if (Math.random() < probabilityPerTick) {
                engineRef.current.trad.generateOrder(flow);
                engineRef.current.ai.generateOrder(flow);
              }
            } else {
              // Constant (Evenly Distributed)
              const targetTicks = (3600 / ratePerHour) * 60;
              if (!orderTicksRef.current[flow.id]) orderTicksRef.current[flow.id] = 0;
              orderTicksRef.current[flow.id]++;
              
              if (orderTicksRef.current[flow.id] >= targetTicks) {
                engineRef.current.trad.generateOrder(flow);
                engineRef.current.ai.generateOrder(flow);
                orderTicksRef.current[flow.id] -= targetTicks;
              }
            }
          }
        } else {
           const probabilityPerTick = (config.orderRate / 3600) / 60;
           if (Math.random() < probabilityPerTick) {
              engineRef.current.trad.generateOrder();
              engineRef.current.ai.generateOrder();
           }
        }
        
        engineRef.current.trad.tick();
        engineRef.current.ai.tick();
        
        if (engineRef.current.trad.metrics.ticks % 10 === 0) {
          const currentTrad = engineRef.current.trad.getState().metrics;
          const currentAi = engineRef.current.ai.getState().metrics;
          
          setMetrics({ trad: currentTrad, ai: currentAi });

          // Save history every 60 ticks (approx 1 sec in 1x)
          if (engineRef.current.trad.metrics.ticks % 60 === 0) {
            setMetricsHistory(prev => {
              const time = Math.floor(currentTrad.ticks / 60);
              return [...prev, {
                time,
                tradThroughput: currentTrad.throughput,
                aiThroughput: currentAi.throughput,
                tradWait: currentTrad.totalWaitTime,
                aiWait: currentAi.totalWaitTime,
                tradBlock: currentTrad.totalBlockTime,
                aiBlock: currentAi.totalBlockTime,
              }];
            });
          }
        }
      }
      
      drawEngineState(tradCanvasRef.current, engineRef.current.trad);
      drawEngineState(aiCanvasRef.current, engineRef.current.ai);
    }

    if (isPlaying) {
      reqRef.current = requestAnimationFrame(tick);
    }
  };

  useEffect(() => {
    if (isPlaying) {
      reqRef.current = requestAnimationFrame(tick);
    } else if (engineRef.current.trad && engineRef.current.ai) {
      // Draw initial state even when paused
      drawEngineState(tradCanvasRef.current, engineRef.current.trad);
      drawEngineState(aiCanvasRef.current, engineRef.current.ai);
    }
    return () => cancelAnimationFrame(reqRef.current);
  }, [isPlaying, bgImage]);

  if (mapData.nodes.length === 0) {
    return (
      <div className="flex h-full items-center justify-center text-gray-500 flex-col">
        <p className="mb-2">No map data available.</p>
        <p className="text-sm">Please go to Map Setup and draw nodes and paths first.</p>
      </div>
    );
  }

  const validFlows = (mapData.flows || []).filter(f => 
    (f.sourceAreaId || (f.sourceAreaIds && f.sourceAreaIds.length > 0)) && 
    (f.targetAreaId || (f.targetAreaIds && f.targetAreaIds.length > 0))
  );
  
  let simulationWarnings = [];
  let canRun = false;

  if (validFlows.length > 0) {
    let validPathsCount = 0;
    validFlows.forEach(f => {
      const srcIds = f.sourceAreaIds || (f.sourceAreaId ? [f.sourceAreaId] : []);
      const tgtIds = f.targetAreaIds || (f.targetAreaId ? [f.targetAreaId] : []);
      
      const srcNodes = mapData.nodes.filter(n => srcIds.includes(n.connectedAreaId) || (n.connectedAreaIds && n.connectedAreaIds.some(id => srcIds.includes(id))));
      const tgtNodes = mapData.nodes.filter(n => tgtIds.includes(n.connectedAreaId) || (n.connectedAreaIds && n.connectedAreaIds.some(id => tgtIds.includes(id))));
      
      if (srcNodes.length === 0) {
        simulationWarnings.push(`A Source area in your material flow is missing an AGV Corridor connection.`);
      }
      if (tgtNodes.length === 0) {
        simulationWarnings.push(`A Target area in your material flow is missing an AGV Corridor connection.`);
      }
      if (srcNodes.length > 0 && tgtNodes.length > 0) {
        validPathsCount++;
      }
    });
    canRun = validPathsCount > 0;
  } else {
    const hasPickup = mapData.areas.some(a => a.type === 'pickup');
    const hasStorage = mapData.areas.some(a => a.type === 'storage');
    if (!hasPickup || !hasStorage) {
      simulationWarnings.push("Define Material Flows or create at least 1 Pickup and 1 Storage area!");
    } else {
       const pickupNodes = mapData.nodes.filter(n => n.type === 'pickup');
       const storageNodes = mapData.nodes.filter(n => n.type === 'storage');
       if (pickupNodes.length === 0) simulationWarnings.push("Pickup area is missing an AGV Corridor connection.");
       if (storageNodes.length === 0) simulationWarnings.push("Storage area is missing an AGV Corridor connection.");
       canRun = pickupNodes.length > 0 && storageNodes.length > 0;
    }
  }

  simulationWarnings = [...new Set(simulationWarnings)]; // Deduplicate

  return (
    <div className="flex flex-col h-full bg-white rounded-lg">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center space-x-4">
          <h2 className="text-xl font-bold">Simulation Arena</h2>
          <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-md border shadow-inner">
            <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            <span className="text-lg font-mono font-medium text-gray-700 tracking-wider">
              {formatTime(metrics.trad?.ticks || 0)}
            </span>
          </div>
          {simulationWarnings.length > 0 && (
            <div className="flex items-center space-x-1 text-red-600 bg-red-50 px-3 py-1 rounded-md text-sm font-semibold">
               <span className="mr-1">⚠️</span>
               {simulationWarnings.join(' ')}
            </div>
          )}
        </div>
         <div className="flex items-center gap-4">
            <div className="flex bg-gray-200 rounded-md p-1">
              <button 
                onClick={() => setViewMode('2d')} 
                className={`px-3 py-1 rounded-sm text-sm font-bold ${viewMode === '2d' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
                2D Map
              </button>
              <button 
                onClick={() => setViewMode('3d')} 
                className={`px-3 py-1 rounded-sm text-sm font-bold ${viewMode === '3d' ? 'bg-white shadow-sm text-gray-800' : 'text-gray-500 hover:text-gray-700'}`}>
                3D Digital Twin
              </button>
            </div>
            {!canRun && (
               <span className="text-sm text-gray-500 italic">Fix setup to start</span>
            )}
            <button 
             onClick={() => setIsPlaying(!isPlaying)}
             disabled={!canRun}
             className={`px-6 py-2 rounded-md text-white font-medium shadow-sm transition-colors ${!canRun ? 'bg-gray-300 cursor-not-allowed' : (isPlaying ? 'bg-red-500 hover:bg-red-600' : 'bg-green-400 hover:bg-green-500')}`}
           >
             {isPlaying ? 'Pause Simulation' : 'Start Simulation'}
           </button>
         </div>
       </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Traditional Side */}
        <div className="flex-1 flex flex-col border-r">
          <div className="bg-gray-100 p-2 text-center font-bold border-b text-gray-600">
             Traditional Rule-Based
          </div>
          <div className="flex-1 p-4 flex items-center justify-center bg-gray-50 overflow-hidden relative">
             {viewMode === '2d' ? (
               <canvas ref={tradCanvasRef} width={800} height={600} className="w-full h-auto object-contain bg-white shadow-sm border border-gray-200" style={{ maxHeight: '100%', maxWidth: '100%' }} />
             ) : (
               <div className="absolute inset-4 rounded overflow-hidden shadow-inner border border-gray-300">
                 <SimulationView3D mapData={mapData} engine={engineRef.current.trad} config={config} />
               </div>
             )}
          </div>
          <div className="p-4 bg-white border-t text-sm">
            <div className="grid grid-cols-2 gap-4">
               <div className="bg-gray-50 p-4 rounded border flex flex-col justify-center items-center shadow-sm">
                 <div className="text-sm font-bold text-gray-500 mb-1 uppercase">Throughput</div>
                 <div className="text-3xl font-bold">
                   {metrics.trad?.throughput || 0} <span className="text-sm font-normal text-gray-500">pallets</span>
                 </div>
               </div>
               <div className="bg-gray-50 p-4 rounded border flex flex-col justify-center items-center shadow-sm">
                 <div className="text-sm font-bold text-gray-500 mb-1 uppercase">Wait Time</div>
                 <div className="text-3xl font-bold text-gray-700">{formatTicksToMins(metrics.trad?.totalWaitTime)}</div>
               </div>
               <div className="bg-gray-50 p-2.5 rounded col-span-2 flex justify-between items-center border">
                 <div className="text-xs text-gray-500 uppercase font-bold">Block / Deadhead Time</div>
                 <div className="text-sm font-bold text-red-500">{formatTicksToMins(metrics.trad?.totalBlockTime)}</div>
               </div>
            </div>
          </div>
        </div>

        {/* AI Side */}
        <div className="flex-1 flex flex-col">
          <div className="bg-red-50 p-2 text-center font-bold border-b text-red-700 flex items-center justify-center gap-2">
             T-One Centralized AI
          </div>
          <div className="flex-1 p-4 flex items-center justify-center bg-gray-50 overflow-hidden relative">
             {viewMode === '2d' ? (
               <canvas ref={aiCanvasRef} width={800} height={600} className="w-full h-auto object-contain bg-white shadow-sm border border-gray-200" style={{ maxHeight: '100%', maxWidth: '100%' }} />
             ) : (
               <div className="absolute inset-4 rounded overflow-hidden shadow-inner border border-red-200">
                 <SimulationView3D mapData={mapData} engine={engineRef.current.ai} config={config} />
               </div>
             )}
          </div>
          <div className="p-4 bg-white border-t text-sm">
            <div className="grid grid-cols-2 gap-4">
               <div className="bg-red-50 p-4 rounded border border-red-100 flex flex-col justify-center items-center shadow-sm">
                 <div className="text-sm font-bold text-red-600 mb-1 uppercase">Throughput</div>
                 <div className="text-3xl font-bold text-red-700">
                   {metrics.ai?.throughput || 0} <span className="text-sm font-normal text-red-500">pallets</span>
                 </div>
               </div>
               <div className="bg-red-50 p-4 rounded border border-red-100 flex flex-col justify-center items-center shadow-sm">
                 <div className="text-sm font-bold text-red-500 mb-1 uppercase">Wait Time</div>
                 <div className="text-3xl font-bold text-red-700">{formatTicksToMins(metrics.ai?.totalWaitTime)}</div>
               </div>
               <div className="bg-green-50 p-2.5 rounded col-span-2 border border-green-100 flex justify-between items-center shadow-sm">
                 <div className="text-xs text-green-600 uppercase font-bold">Fluid Movement (Zero Block)</div>
                 <div className="text-sm font-bold text-green-700">{formatTicksToMins(metrics.ai?.totalBlockTime)}</div>
               </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
